import React from 'react';
import { useTranslation } from 'react-i18next';

const getRoleLabels = (t) => ({
  admin: t('roles.admin'),
  operator: t('roles.operator'),
  user: t('roles.user'),
  viewer: t('roles.viewer'),
  engineer: t('roles.engineer'),
  demo: t('roles.demo'),
});

/**
 * Генерирует аббревиатуру из email
 * @param {string} email - Email пользователя
 * @returns {string} - Двухбуквенная аббревиатура
 */
export const getInitials = (email) => {
  if (!email || typeof email !== 'string') return 'MK';
  
  // Убираем домен и получаем локальную часть email
  const localPart = email.split('@')[0];
  
  // Если есть точка, берем первые буквы до и после точки
  if (localPart.includes('.')) {
    const parts = localPart.split('.');
    if (parts.length >= 2) {
      const first = parts[0].charAt(0).toUpperCase();
      const second = parts[1].charAt(0).toUpperCase();
      return first + second;
    }
  }
  
  // Если есть дефис или подчеркивание
  if (localPart.includes('-')) {
    const parts = localPart.split('-');
    if (parts.length >= 2) {
      const first = parts[0].charAt(0).toUpperCase();
      const second = parts[1].charAt(0).toUpperCase();
      return first + second;
    }
  }
  
  if (localPart.includes('_')) {
    const parts = localPart.split('_');
    if (parts.length >= 2) {
      const first = parts[0].charAt(0).toUpperCase();
      const second = parts[1].charAt(0).toUpperCase();
      return first + second;
    }
  }
  
  // Если просто имя, берем первые две буквы
  if (localPart.length >= 2) {
    return localPart.substring(0, 2).toUpperCase();
  }
  
  // Fallback на первую букву
  return localPart.charAt(0).toUpperCase();
};

/**
 * Получает email пользователя из пропсов или localStorage
 * @param {string} userEmail - Email из пропсов
 * @returns {string} - Email пользователя
 */
export const getUserEmail = (userEmail) => {
  if (userEmail) return userEmail;
  return localStorage.getItem('userEmail') || '';
};

/**
 * Получает роль пользователя из пропсов или localStorage
 * @param {string} userRole - Роль из пропсов
 * @returns {string} - Роль пользователя
 */
export const getUserRole = (userRole) => {
  if (userRole) return userRole;
  return localStorage.getItem('userRole') || '';
};

/**
 * Получает метку роли для отображения
 * @param {string} userRole - Роль пользователя
 * @param {Function} t - Функция перевода (опционально)
 * @returns {string} - Метка роли
 */
export const getRoleLabel = (userRole, t = null) => {
  if (!userRole) return null;
  const normalizedRole = userRole.trim().toLowerCase();
  if (t) {
    const roleLabels = getRoleLabels(t);
    return roleLabels[normalizedRole] || userRole;
  }
  // Fallback для случаев, когда t не передан
  const fallbackLabels = {
    admin: 'Администратор',
    operator: 'Оператор',
    user: 'Пользователь',
    viewer: 'Наблюдатель',
    engineer: 'Инженер',
  };
  return fallbackLabels[normalizedRole] || userRole;
};

/**
 * Компонент для отображения информации об аккаунте
 * @param {Object} props
 * @param {string} props.userEmail - Email пользователя (опционально, будет взят из localStorage)
 * @param {string} props.userRole - Роль пользователя (опционально, будет взята из localStorage)
 * @param {string} props.size - Размер компонента ('sm' | 'md' | 'lg')
 * @param {boolean} props.showRole - Показывать ли роль
 */
const AccountInfo = ({ userEmail: propUserEmail, userRole: propUserRole, size = 'md', showRole = true }) => {
  const { t } = useTranslation();
  const userEmail = getUserEmail(propUserEmail);
  const userRole = getUserRole(propUserRole);
  const initials = getInitials(userEmail);
  const roleLabel = getRoleLabel(userRole, t);

  const sizeClasses = {
    sm: {
      icon: 'h-8 w-8 text-xs',
      text: 'text-sm',
      subtext: 'text-xs',
    },
    md: {
      icon: 'h-10 w-10 text-sm',
      text: 'text-sm',
      subtext: 'text-xs',
    },
    lg: {
      icon: 'h-12 w-12 text-base',
      text: 'text-lg',
      subtext: 'text-sm',
    },
  };

  const classes = sizeClasses[size] || sizeClasses.md;

  return (
    <div className="flex items-center gap-3">
      <span className={`flex ${classes.icon} items-center justify-center rounded-full bg-brand-linear text-white font-semibold shadow-brand`}>
        {initials}
      </span>
      <div className="flex flex-col">
        <span className={`${classes.text} font-semibold text-white`}>
          {userEmail || t('common.account')}
        </span>
        {showRole && roleLabel && (
          <span className={`${classes.subtext} text-muted-400`}>{roleLabel}</span>
        )}
      </div>
    </div>
  );
};

export default AccountInfo;

